import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { toast } from "sonner"
import { api, type AdminUser } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FieldError } from "@/components/ui/field-error"
import { Button } from "@/components/ui/button"
import { Toaster } from "@/components/ui/sonner"

export const Route = createFileRoute("/admin/login")({
  component: AdminLogin,
  head: () => ({ meta: [{ title: "Admin Login — Nexovia IT" }] }),
})

function AdminLogin() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<AdminUser>("/auth/login", { email, password })
      return res.data
    },
    onSuccess: (user) => {
      queryClient.setQueryData(["auth", "me"], user)
      setFieldErrors({})
      toast.success(`Welcome back, ${user.name}.`)
      navigate({ to: "/admin/dashboard" })
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <Toaster position="top-right" />
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-card">
        <h1 className="font-display text-2xl font-bold text-ink">Nexovia IT</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to the admin panel.</p>
        <form
          className="mt-8 space-y-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            mutation.mutate()
          }}
        >
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!fieldErrors.email}
            />
            <FieldError message={fieldErrors.email} />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={!!fieldErrors.password}
            />
            <FieldError message={fieldErrors.password} />
          </div>
          <Button type="submit" disabled={mutation.isPending} className="w-full rounded-full bg-gradient-accent">
            {mutation.isPending ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  )
}
